import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CodeIcon from "@mui/icons-material/Code";
import LayersIcon from "@mui/icons-material/Layers";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import StorageIcon from "@mui/icons-material/Storage";
import InsightsIcon from "@mui/icons-material/Insights";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ Web Development Content Data
const webDevContent = [
  {
    section: "Frontend Development",
    icon: <CodeIcon />,
    items: [
      {
        name: "HTML5",
        description: "Structure and semantic foundation of web apps.",
        fullContent: [
          {
            heading: "Introduction",
            text: "HTML5 is the latest version of HTML. It introduces semantic tags, multimedia support, forms, APIs, and advanced features for modern web apps.",
          },
          {
            heading: "Basic Structure",
            code: `<!DOCTYPE html>
<html>
  <head>
    <title>My First HTML5 Page</title>
  </head>
  <body>
    <h1>Welcome to HTML5!</h1>
    <p>This is a basic HTML5 page.</p>
  </body>
</html>`,
          },
        ],
      },
      {
        name: "CSS3",
        description: "Modern styling, responsive layouts, and animations.",
        fullContent: [
          {
            heading: "Flexbox Layout",
            text: "CSS Flexbox allows you to design a flexible responsive layout structure without using float or positioning.",
            code: `.container {
  display: flex;
  justify-content: center;
  align-items: center;
}`,
          },
        ],
      },
      {
        name: "JavaScript (ES6+)",
        description: "Dynamic interactivity and modern features.",
        fullContent: [
          {
            heading: "Modern Features",
            text: "ES6 introduced classes, modules, arrow functions, and more.",
            code: `const greet = (name) => {
  return \`Hello, \${name}!\`;
};
console.log(greet('World'));`,
          },
        ],
      },
    ],
  },
  {
    section: "Frameworks & Libraries",
    icon: <LayersIcon />,
    items: [
      {
        name: "React.js",
        description: "A library for building user interfaces.",
        fullContent: [
          {
            heading: "Components",
            text: "React allows you to split usage into independent, reusable pieces.",
            code: `function Welcome(props) {
  return <h1>Hello, {props.name}</h1>;
}`,
          },
        ],
      },
      {
        name: "Next.js",
        description: "The React Framework for Production.",
        fullContent: [
          {
            heading: "Server-Side Rendering",
             text: "Next.js enables you to do server-side rendering and generating static websites for React based web applications.",
          },
        ],
      },
       {
        name: "Angular",
        description: "Enterprise-ready framework for scalable apps.",
        fullContent: [
          {
            heading: "Components & Templates",
             code: `@Component({
  selector: 'app-root',
  template: '<h1>Hello Angular!</h1>'
})
export class AppComponent { }`,
          },
        ],
      },
    ],
  },
  {
    section: "Backend Development",
    icon: <SettingsApplicationsIcon />,
    items: [
      {
        name: "Node.js",
        description: "JavaScript runtime built on Chrome's V8 engine.",
        fullContent: [
          {
            heading: "HTTP Server",
            code: `const http = require('http');
const server = http.createServer((req, res) => {
  res.statusCode = 200;
  res.end('Hello World');
});
server.listen(3000);`,
          },
        ],
      },
      {
        name: "Express.js",
        description: "Fast, unopinionated, minimalist web framework.",
        fullContent: [
          {
            heading: "Routing",
            code: `const express = require('express')
const app = express()
app.get('/', (req, res) => res.send('Hello World!'))
app.listen(3000)`,
          },
        ],
      },
      {
        name: "Django",
        description: "The web framework for perfectionists with deadlines.",
        fullContent: [
          {
            heading: "Views",
            code: `from django.http import HttpResponse

def index(request):
    return HttpResponse("Hello, world.")`,
          },
        ],
      },
    ],
  },
  {
    section: "Databases",
    icon: <StorageIcon />,
    items: [
      {
        name: "MongoDB",
        description: "NoSQL document-based database.",
        fullContent: [
           {
             heading: "Documents",
             text: "MongoDB stores data in flexible, JSON-like documents.",
             code: `db.collection.insertOne({ item: "canvas", qty: 100 })`
           }
        ],
      },
      {
        name: "MySQL",
        description: "The world's most popular open source database.",
        fullContent: [
          {
            heading: "Select Query",
            code: `SELECT * FROM Customers;`,
          },
        ],
      },
      {
         name: "PostgreSQL",
         description: "Advanced open-source relational database.",
         fullContent: [
           {
             heading: "Features",
             text: "PostgreSQL is a powerful, open source object-relational database system.",
           }
         ]
       }
    ],
  },
  {
    section: "Advanced Topics",
    icon: <InsightsIcon />,
    items: [
        {
            name: "GraphQL",
            description: "A query language for your API.",
            fullContent: [
                {
                    heading: "Schema Definition",
                    code: `type Query {
  me: User
}`
                }
            ]
        },
        {
            name: "Microservices",
            description: "Architectural style for developing applications.",
            fullContent: [
                {
                    heading: "Concept",
                    text: "Microservices structure an application as a collection of services."
                }
            ]
        },
        {
            name: "Serverless",
            description: "Build and run applications without thinking about servers.",
            fullContent: [
                {
                    heading: "AWS Lambda Example",
                    code: `exports.handler = async (event) => {
    const response = {
        statusCode: 200,
        body: JSON.stringify('Hello from Lambda!'),
    };
    return response;
};`
                }
            ]
        }
    ]
  }
];

function WebDevelopment() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Web Development
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {webDevContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {webDevContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {webDevContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {webDevContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language="javascript"
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default WebDevelopment;